import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Receipt, Plus, Search, Loader2, ChevronRight, ChevronsLeft,
  Landmark, FileDown, AlertCircle, AlertTriangle, CircleCheck, Circle, Paperclip, Building2, Star, X, Banknote
} from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson, apiDelete } from '../lib/api.js'
import { useTypeahead } from '../lib/useTypeahead.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { useMe } from '../lib/me.js'
import { usePersistedState } from '../lib/persist.js'
import { asList, INPUT } from '../pages/statistic/statShared.jsx'
import { fmtMoney, TH_MONTHS, TH_MONTHS_ABBR } from '../lib/expense.js'
import ExpenseEditor from '../components/expense/ExpenseEditor.jsx'
import RecurringDrawer from '../components/expense/RecurringDrawer.jsx'
import ExpenseTrendChart from '../components/expense/ExpenseTrendChart.jsx'
import ExpenseScbModal from '../components/expense/ExpenseScbModal.jsx'
import { BANK_CODE, ISSUE_LABEL } from '../lib/scbBulk.js'
import Pager from '../components/Pager.jsx'

const YEARS = (() => {
  const y = new Date().getFullYear()
  return [y + 1, y, y - 1, y - 2, y - 3]
})()

const PER_PAGE = 50

function StatusBadge({ paid }) {
  return paid ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 text-emerald-600 px-2 py-0.5 text-[11.5px] font-medium">
      <CircleCheck size={12} strokeWidth={2.2} className="shrink-0" /> จ่ายแล้ว
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[11.5px] font-medium">
      <Circle size={12} strokeWidth={2.2} className="shrink-0" /> รอจ่าย
    </span>
  )
}

// What a row needs before the SCB file can carry it, in the words the operator
// has to act on. Keyed by the `issue` POST /api/expense/scb-bulk returns, so this
// chip and the ข้ามไป list inside the export dialog can never disagree.
const ISSUE_FIX = {
  no_seller:        'เปิดรายการนี้ แล้วผูกกับบริษัทผู้ขายในระบบ',
  no_bank:          'เปิดรายการนี้ → บริษัทในระบบ แล้วเติมธนาคาร',
  no_account:       'เปิดรายการนี้ → บริษัทในระบบ แล้วเติมเลขที่บัญชี',
  unknown_bank:     'ชื่อธนาคารในข้อมูลบริษัทผู้ขายไม่ตรงกับรายชื่อของธนาคาร',
  no_debit_account: 'บริษัทของสาขานี้ยังไม่มีบัญชีตัดเงิน SCB',
  net_not_positive: 'ยอดชำระต้องมากกว่า 0 จึงจะโอนได้',
}

// The flag on a รอจ่าย row that the transfer file will silently leave out.
// Deliberately loud: before this existed the only place the skip was visible was
// inside the export dialog, so a row could sit unpaid for months without anyone
// being told why it never appeared on a statement.
function TransferIssue({ issue }) {
  if (!issue || issue === 'already_paid') return null
  return (
    <span
      title={`ไฟล์โอนเงิน SCB จะข้ามรายการนี้ — ${ISSUE_FIX[issue] || ''}`}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200"
    >
      <AlertTriangle size={11} strokeWidth={2.2} className="shrink-0" />
      {ISSUE_LABEL[issue] || issue}
    </span>
  )
}

function Tag({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-500',
    indigo: 'bg-indigo-50 text-indigo-600',
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
  }
  return <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>
}

const TILE_TONES = {
  brand:   { dot: 'bg-brand-500',   activeBg: 'bg-brand-50',    activeRing: 'ring-brand-300' },
  emerald: { dot: 'bg-emerald-500', activeBg: 'bg-emerald-50',  activeRing: 'ring-emerald-300' },
  slate:   { dot: 'bg-slate-400',   activeBg: 'bg-slate-100',   activeRing: 'ring-slate-300' },
}
// Searchable seller/company filter — queries /api/sellers live; on pick, filters the
// expense list by the seller's integer id (the Xano `seller` param).
function SellerFilter({ value, valueName, onPick, onClear }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  useEffect(() => {
    if (!q.trim()) { setRows([]); return }
    let alive = true; setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/sellers', { search: q.trim(), per_page: 20 })
        .then((r) => { if (alive) setRows(Array.isArray(r) ? r : (r?.items || [])) })
        .catch(() => { if (alive) setRows([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q])

  // Keyboard nav: only the real seller rows are indexable (rendered when !loading).
  const count = loading ? 0 : rows.length
  const ta = useTypeahead({
    count,
    onSelect: (i) => {
      const s = rows[i]
      if (!s) return
      const id = s.id ?? s.seller_id
      const name = s.seller_name || s.name || '(ไม่มีชื่อ)'
      onPick(id, name); setOpen(false); setQ('')
    },
    onClose: () => setOpen(false),
  })

  if (value) {
    return (
      <div className="relative flex-1 min-w-[180px]">
        <Building2 size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-500" />
        <div className={`${INPUT} w-full pl-8 pr-8 truncate bg-brand-50/50 border-brand-200 text-brand-700`}>{valueName || `#${value}`}</div>
        <button onClick={onClear} title="ล้างตัวกรองบริษัท" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"><X size={14} /></button>
      </div>
    )
  }
  return (
    <div className="relative flex-1 min-w-[180px]" ref={ref}>
      <Building2 size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={ta.onKeyDown}
        placeholder="ค้นหาบริษัท / ผู้ขาย…"
        className={`${INPUT} w-full pl-8`}
      />
      {open && q.trim() && (
        <div ref={ta.listRef} className="absolute z-30 mt-1.5 w-full max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1">
          {loading && <div className="px-3 py-2 text-[13px] text-slate-400"><Loader2 size={13} className="inline animate-spin mr-1.5" /> กำลังค้นหา…</div>}
          {!loading && !rows.length && <div className="px-3 py-2 text-[13px] text-slate-400">ไม่พบบริษัท</div>}
          {!loading && rows.map((s, i) => {
            const id = s.id ?? s.seller_id
            const name = s.seller_name || s.name || '(ไม่มีชื่อ)'
            const taxid = s.seller_taxid || s.taxid || s.tax_id || ''
            return (
              <button
                key={id ?? name}
                type="button"
                data-ti-index={i}
                onMouseEnter={() => ta.setActiveIndex(i)}
                onMouseDown={(e) => { e.preventDefault(); onPick(id, name); setOpen(false); setQ('') }}
                className={`flex w-full items-center justify-between gap-2 text-left px-3 py-2 transition ${i === ta.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50'}`}
              >
                <span className="text-[14px] text-slate-700 truncate">{name}</span>
                {taxid && <span className="text-[11.5px] text-slate-400 tabular-nums shrink-0">{taxid}</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StatTile({ active, onClick, label, count, sum, tone }) {
  const t = TILE_TONES[tone] || TILE_TONES.brand
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 transition ${active ? `${t.activeBg} border-transparent ring-2 ${t.activeRing}` : 'bg-white border-slate-200 hover:border-slate-300'}`}
    >
      <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        {label}
        <span className="text-slate-400">· {count.toLocaleString()} รายการ</span>
      </div>
      <div className="mt-1 text-[22px] font-bold text-slate-900 tabular-nums">{fmtMoney(sum)}</div>
    </button>
  )
}

export default function Expense() {
  const [branchName, setBranchName] = useState({})
  const [search, setSearch] = usePersistedState('exp:search', '')
  const [year, setYear] = usePersistedState('exp:year', String(new Date().getFullYear()))
  // Default to the CURRENT month (accounting is monthly) — a whole-year default made
  // the list fetch a huge set. Key bumped to :v2 so the new default takes effect.
  const [month, setMonth] = usePersistedState('exp:month:v2', () => String(new Date().getMonth() + 1)) // '' = ทั้งปี
  const [branch, setBranch] = usePersistedState('exp:branch', '') // '' = ทุกสาขา
  const [wht, setWht] = usePersistedState('exp:wht', false)
  const [petty, setPetty] = usePersistedState('exp:petty', false)
  const [paidFilter, setPaidFilter] = usePersistedState('exp:paid', 'all') // all | paid | unpaid
  const [page, setPage] = useState(1)

  const [allItems, setAllItems] = useState([]) // every record for the current filter (fetched across pages)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [editing, setEditing] = useState(null) // { mode:'create'|'edit', id? }

  // Deep link — /expense?expense_id=412 opens that record straight away. Used by
  // the จัดซื้อ card's ค่าใช้จ่าย tag, so a PO can hand you its expense without
  // hunting for it through the month/branch filters. The editor loads by id, so
  // the record does not have to be in the current filtered page at all.
  //
  // The param is consumed once and stripped from the URL: leaving it there would
  // reopen the drawer every time this page re-renders after you close it.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const id = Math.trunc(Number(searchParams.get('expense_id')) || 0)
    if (!(id > 0)) return
    setEditing({ mode: 'edit', id })
    const next = new URLSearchParams(searchParams)
    next.delete('expense_id')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const me = useMe()
  const liveTimer = useRef(null)
  const [editChangeTick, setEditChangeTick] = useState(0) // bumps when the open drawer's record changes elsewhere

  // Debounce the free-text search into the query.
  const [q, setQ] = useState(search)
  useEffect(() => { const t = setTimeout(() => setQ(search), 350); return () => clearTimeout(t) }, [search])

  // Seller filter — a picked seller id (the Xano `seller` param is an integer FK).
  const [sellerId, setSellerId] = usePersistedState('exp:sellerId', '')
  const [sellerName, setSellerName] = usePersistedState('exp:sellerName', '')

  // Branch id→name map.
  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
  }, [])

  // Reset to page 1 whenever a filter (including the paid selector) changes.
  useEffect(() => { setPage(1) }, [q, sellerId, year, month, branch, wht, petty, paidFilter])

  // Load ALL records for the current filter (across pages) so the header stats +
  // paid selector are accurate over the whole set, not just one page. Volumes are
  // modest (accounting is monthly); a 25-page safety cap guards a runaway year view.
  const load = useCallback(async () => {
    setLoading(true); setError(null)
    const params = {
      per_page: 200,
      active: 'true',
      search: q || undefined,
      seller: sellerId || undefined, // integer seller_id (picked from the dropdown)
      year: year || undefined,
      month: month || undefined,
      branch_id: branch || undefined,
      WHT: wht ? 'true' : undefined,
      petty_cash: petty ? 'true' : undefined,
    }
    try {
      // Page 1 tells us pageTotal; fetch any remaining pages in PARALLEL (not
      // sequentially) so a multi-page filter isn't N×round-trips. Bounded by a
      // 25-page cap; dedupe by id as a backstop against a re-returning feed.
      const first = await apiGet('/api/expense', { ...params, page: 1 })
      const acc = asList(first)
      const totalPages = Math.max(1, Number(first?.pageTotal) || 1)
      const cap = Math.min(totalPages, 25)
      if (cap > 1 && acc.length > 0) {
        const rest = await Promise.all(
          Array.from({ length: cap - 1 }, (_, i) =>
            apiGet('/api/expense', { ...params, page: i + 2 }).catch(() => null)),
        )
        for (const r of rest) acc.push(...asList(r))
      }
      const seen = new Set(); const out = []
      for (const e of acc) { const id = e?.id; if (id != null) { if (seen.has(id)) continue; seen.add(id) } out.push(e) }
      setAllItems(out); setTruncated(totalPages > 25)
    } catch (e) {
      setError(e.message || 'โหลดข้อมูลไม่สำเร็จ'); setAllItems([])
    } finally {
      setLoading(false)
    }
  }, [q, sellerId, year, month, branch, wht, petty])

  useEffect(() => { load() }, [load])

  // Monthly expense-total trend (history) for the top chart. Respects the branch filter.
  const [trend, setTrend] = useState([])
  const [trendLoading, setTrendLoading] = useState(true)
  useEffect(() => {
    let alive = true; setTrendLoading(true)
    apiGet('/api/expense', { mode: 'chart', active: 'true', branch_id: branch || undefined })
      .then((r) => { if (alive) setTrend(asList(r)) })
      .catch(() => { if (alive) setTrend([]) })
      .finally(() => { if (alive) setTrendLoading(false) })
    return () => { alive = false }
  }, [branch])

  // Recurring series (all months) — used for the "to-record this month" checklist.
  // Favorite templates (expense_recurring) + this-year's linked instances (to know
  // which months each template has already been recorded for).
  const [recurTemplates, setRecurTemplates] = useState([])
  const [recurInstances, setRecurInstances] = useState([])
  const loadRecurring = useCallback(async () => {
    try {
      const [tpls] = await Promise.all([
        apiGet('/api/expense-recurring', { per_page: 200 }).then(asList).catch(() => []),
      ])
      setRecurTemplates(tpls.filter((t) => t.active !== false))
      // Linked instances: fetch the year's active expenses, keep those with recurring_id.
      const gy = new Date().getFullYear()
      const base = { active: 'true', year: gy, per_page: 200 }
      const first = await apiGet('/api/expense', { ...base, page: 1 })
      const acc = asList(first)
      const totalPages = Math.max(1, Number(first?.pageTotal) || 1)
      const cap = Math.min(totalPages, 15)
      if (cap > 1 && acc.length) {
        const rest = await Promise.all(
          Array.from({ length: cap - 1 }, (_, i) => apiGet('/api/expense', { ...base, page: i + 2 }).catch(() => null)),
        )
        for (const r of rest) acc.push(...asList(r))
      }
      setRecurInstances(acc.filter((e) => Number(e.recurring_id) > 0))
    } catch { setRecurTemplates([]); setRecurInstances([]) }
  }, [])
  useEffect(() => { loadRecurring() }, [loadRecurring])

  // Each favorite template + which periods (year*100+month) already have a linked expense.
  const recurringSeries = useMemo(() => {
    const byTpl = new Map() // templateId -> Map(period -> expenseId)
    for (const e of recurInstances) {
      const tid = Number(e.recurring_id) || 0
      if (!tid) continue
      if (!byTpl.has(tid)) byTpl.set(tid, new Map())
      const per = (Number(e.expense_year) || 0) * 100 + (Number(e.expense_month_num) || 0)
      byTpl.get(tid).set(per, e.id)
    }
    return recurTemplates.map((t) => ({
      key: String(t.id),
      templateId: t.id,
      template: t,
      branchId: t.branch_id,
      detail: t.detail || '',
      amount: t.amount?.payment || 0,
      byPeriod: byTpl.get(t.id) || new Map(),
    }))
  }, [recurTemplates, recurInstances])

  // Badge count: favorites not yet recorded for the current-view month (+ branch).
  const now = new Date()
  const badgePer = (year ? Number(year) : now.getFullYear()) * 100 + (month ? Number(month) : now.getMonth() + 1)
  const recurringDueCount = useMemo(
    () => recurringSeries.filter((s) => (!branch || String(s.branchId) === String(branch)) && !s.byPeriod.has(badgePer)).length,
    [recurringSeries, badgePer, branch],
  )

  // Create a new expense for (ty, tm) from a favorite template; link it back. Returns newId.
  const [creatingKey, setCreatingKey] = useState(null)
  const createRecurring = useCallback(async (series, ty, tm) => {
    const t = series.template
    setCreatingKey(series.key)
    try {
      const db = `${ty}-${String(tm).padStart(2, '0')}-01`
      const uid = (() => { try { return crypto.randomUUID() } catch { return 'x' + Date.now().toString(36) } })()
      const created = await postJson('/api/expense', {
        detail: t.detail || '',
        branch_id: t.branch_id || 0,
        date_buy: db, date_display: db,
        expense_year: ty, expense_month_num: tm, expense_month: TH_MONTHS_ABBR[tm - 1] || '',
        amount: t.amount || {},
        remark: t.remark || '',
        tax: t.tax !== false, asset: !!t.asset, petty_cash: !!t.petty_cash,
        paid: false,
        seller: t.seller || {},
        billing: (Array.isArray(t.billing) ? t.billing : []).map((b) => ({ ...b })),
        file: [],
        recurring: true, recurring_id: t.id,
        active: true, unique_id: uid,
      })
      const newId = created?.id ?? created?.expense?.id ?? null
      if (newId && (t.asset || t.petty_cash)) {
        try { await patchJson('/api/expense/' + newId, { petty_cash: !!t.petty_cash, asset: !!t.asset }) } catch { /* non-fatal */ }
      }
      setYear(String(ty)); setMonth(String(tm))
      loadRecurring()
      return newId
    } catch (e) {
      setError('สร้างจากรายการโปรดไม่สำเร็จ: ' + (e.message || e)); return null
    } finally { setCreatingKey(null) }
  }, [loadRecurring, setYear, setMonth])

  // Remove a favorite (delete its template row). Existing expenses are untouched.
  const removeRecurring = useCallback(async (series) => {
    // apiDelete, not a bare fetch: fetch resolves on a 4xx/5xx, so a refused delete
    // used to reload the list and look like it worked. apiDelete throws on !ok, so
    // the row stays and the error shows.
    try {
      await apiDelete('/api/expense-recurring/' + series.templateId)
      loadRecurring()
    } catch (e) { setError('ลบรายการโปรดไม่สำเร็จ: ' + (e.body?.detail || e.body?.message || e.message || e)) }
  }, [loadRecurring])

  // Recurring manager drawer + "add all" queue (each opens in the editor to confirm).
  const [recurringOpen, setRecurringOpen] = useState(false)
  const [recurQueue, setRecurQueue] = useState(null) // { remaining: series[], ty, tm } | null
  const advanceQueue = useCallback(async (queue) => {
    if (!queue || !queue.remaining.length) { setRecurQueue(null); setEditing(null); return }
    const [head, ...tail] = queue.remaining
    setRecurQueue({ remaining: tail, ty: queue.ty, tm: queue.tm })
    const newId = await createRecurring(head, queue.ty, queue.tm)
    setEditing(newId ? { mode: 'edit', id: newId } : null)
  }, [createRecurring])
  const addRecurringOne = useCallback(async (series, ty, tm) => {
    setRecurringOpen(false)
    const newId = await createRecurring(series, ty, tm)
    setEditing(newId ? { mode: 'edit', id: newId } : null)
  }, [createRecurring])
  const addRecurringAll = useCallback((missing, ty, tm) => {
    setRecurringOpen(false)
    advanceQueue({ remaining: missing, ty, tm })
  }, [advanceQueue])

  // Realtime: another user creating / editing / deleting an expense broadcasts to
  // the anonymous `expense_update` channel; refetch the current view (debounced).
  // Skip our own edits (already refetched via onSaved) and, when a single branch
  // is in view, changes to other branches. Payload shape: { employee_id, branch_id }.
  useXanoRealtime('expense_update', (msg) => {
    const p = (msg && msg.payload) || {}
    // Xano's Realtime Event wraps our object under payload.data
    // ({ data: { employee_id, branch_id }, dbo_id, row_id }); fall back to p itself.
    const d = (p && typeof p.data === 'object' && p.data) ? p.data : p
    const emp = d?.employee_id
    if (emp != null && me?.employee_id != null && String(emp) === String(me.employee_id)) return // our own edit — already refreshed
    // If the open drawer is showing this exact record, flag it stale (any branch).
    const changedId = d?.expense_id ?? d?.id ?? p?.row_id
    if (editing && editing.mode === 'edit' && changedId != null && String(changedId) === String(editing.id)) {
      setEditChangeTick((t) => t + 1)
    }
    if (branch && d?.branch_id != null && String(d.branch_id) !== String(branch)) return // other branch, not in view
    if (liveTimer.current) clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(() => load(), 400) // coalesce bursts into one refetch
  })
  useEffect(() => () => { if (liveTimer.current) clearTimeout(liveTimer.current) }, [])

  const branches = useMemo(
    () => Object.entries(branchName).sort((a, b) => Number(a[0]) - Number(b[0])),
    [branchName],
  )

  // Header stats over the whole filtered set.
  const stats = useMemo(() => {
    let allSum = 0, paidSum = 0, unpaidSum = 0, paidCount = 0, unpaidCount = 0
    for (const e of allItems) {
      const v = Number(e?.amount?.payment) || 0
      allSum += v
      if (e.paid) { paidSum += v; paidCount++ } else { unpaidSum += v; unpaidCount++ }
    }
    return { allSum, paidSum, unpaidSum, allCount: allItems.length, paidCount, unpaidCount }
  }, [allItems])

  /* ── Transfer-readiness of the รอจ่าย rows ────────────────────────────────
   * The list payload cannot answer this on its own: `expense.seller` is a frozen
   * snapshot carrying name/taxid/phone/address and no bank detail at all, so the
   * vendor's account lives only on the seller MASTER. We ask the same endpoint
   * the export dialog asks (one read-only SELECT) and keep the `issue` per id.
   *
   * Scoped to the unpaid rows because that is what the file is built from, and
   * capped at the same 2,000 the proxy accepts — past that the export itself is
   * refused, so flagging rows would be a promise the button cannot keep.
   */
  const unpaidIds = useMemo(
    () => allItems.filter((e) => !e.paid && e?.id != null).map((e) => e.id),
    [allItems],
  )
  // The parent rebuilds the array on every load, so depend on its CONTENT, not
  // its identity — otherwise every realtime refetch re-asks for the same answer.
  const unpaidKey = useMemo(() => unpaidIds.join(','), [unpaidIds])
  const [scbIssues, setScbIssues] = useState({}) // expense_id -> issue key
  useEffect(() => {
    const ids = unpaidKey ? unpaidKey.split(',').map(Number) : []
    if (!ids.length || ids.length > 2000) { setScbIssues({}); return }
    let alive = true
    postJson('/api/expense/scb-bulk', { expense_ids: ids })
      .then((res) => {
        if (!alive) return
        const map = {}
        for (const r of (Array.isArray(res?.rows) ? res.rows : [])) {
          // The server checks paid / seller / account / debit account; the
          // bank-NAME table is the one check only the browser can make, exactly
          // as buildExpenseTransferFiles does it.
          const issue = r.issue || (BANK_CODE[String(r.bank || '').trim()] ? '' : 'unknown_bank')
          if (issue && issue !== 'already_paid') map[r.expense_id] = issue
        }
        setScbIssues(map)
      })
      // Non-fatal: a failed probe means no chips, never a broken list.
      .catch(() => { if (alive) setScbIssues({}) })
    return () => { alive = false }
  }, [unpaidKey])

  const issueCount = useMemo(() => Object.keys(scbIssues).length, [scbIssues])
  const [onlyIssues, setOnlyIssues] = useState(false)
  useEffect(() => { if (!issueCount) setOnlyIssues(false) }, [issueCount])
  // Declared here rather than in the filter-reset effect above: that effect runs
  // before `onlyIssues` exists, and naming it there is a temporal-dead-zone throw.
  useEffect(() => { setPage(1) }, [onlyIssues])

  // Apply the paid selector, then paginate client-side.
  const filtered = useMemo(() => {
    const base = onlyIssues ? allItems.filter((e) => scbIssues[e.id]) : allItems
    if (paidFilter === 'paid') return base.filter((e) => e.paid)
    if (paidFilter === 'unpaid') return base.filter((e) => !e.paid)
    return base
  }, [allItems, paidFilter, onlyIssues, scbIssues])
  const pageTotal = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  useEffect(() => { if (page > pageTotal) setPage(pageTotal) }, [page, pageTotal])
  const items = useMemo(() => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE), [filtered, page])

  function exportAcc() {
    const p = new URLSearchParams()
    if (year) p.set('year', year)
    if (month) p.set('month', month)
    if (branch) p.set('branch_id', branch)
    window.open('/api/expense/acc/download?' + p.toString(), '_blank')
  }

  /* ── SCB bulk-transfer files (ไฟล์โอนเงิน SCB) ──────────────────────────────
   * The unpaid slice of the CURRENT filter — not the current page, and not the
   * paid selector: the button always means "the รอจ่าย ones in this filter",
   * even while จ่ายแล้ว is the selected tile. Scoping to the filter is deliberate:
   * `paid` is not maintained on old records, so "everything unpaid" would sweep
   * up years of bills that were settled and never ticked.
   */
  const [scbOpen, setScbOpen] = useState(false)
  // Prefixes each .txt the way the doctor-fee files are named ("2026-09 SSDC-SCB.txt").
  const periodLabel = year
    ? (month ? `${year}-${String(month).padStart(2, '0')}` : String(year))
    : 'ทั้งหมด'
  const scopeLabel = [
    year ? `ปี ${Number(year) + 543}` : 'ทุกปี',
    month ? TH_MONTHS[Number(month) - 1] : 'ทั้งปี',
    branch ? (branchName[String(branch)] || `สาขา #${branch}`) : 'ทุกสาขา',
    sellerId ? (sellerName || `บริษัท #${sellerId}`) : null,
    q ? `ค้นหา "${q}"` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="px-8 py-7 max-w-[1240px]">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Link to="/manager" className="text-slate-400 hover:text-slate-600" title="กลับ Manager">
            <ChevronsLeft size={20} />
          </Link>
          <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
            <Receipt size={21} strokeWidth={1.75} />
          </div>
          <div>
            <h1 className="text-[21px] font-semibold text-slate-900 tracking-tight leading-tight">ค่าใช้จ่าย</h1>
            <p className="text-[13px] text-slate-500">บันทึกบัญชี รายการซื้อ และบริษัทผู้ขาย</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRecurringOpen(true)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-medium transition ${recurringDueCount > 0 ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
            title="รายการโปรด (ค่าใช้จ่ายที่ใช้บ่อย/ประจำ)"
          >
            <Star size={15} /> รายการโปรด
            {recurringDueCount > 0 && <span className="rounded-full bg-amber-500 text-white text-[11px] font-semibold px-1.5 leading-5 min-w-[20px] text-center">{recurringDueCount}</span>}
          </button>
          <button
            onClick={() => setScbOpen(true)}
            disabled={loading || !unpaidIds.length}
            title={unpaidIds.length
              ? `สร้างไฟล์โอนเงิน SCB จากรายการที่รอจ่าย ${unpaidIds.length} รายการ`
              : 'ไม่มีรายการที่รอจ่ายในตัวกรองนี้'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-600 hover:border-slate-300 disabled:opacity-40 disabled:hover:border-slate-200"
          >
            <Banknote size={15} /> ไฟล์โอนเงิน SCB
            {unpaidIds.length > 0 && (
              <span className="rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold px-1.5 leading-5 min-w-[20px] text-center tabular-nums">{unpaidIds.length}</span>
            )}
          </button>
          <button
            onClick={exportAcc}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-600 hover:border-slate-300"
          >
            <FileDown size={15} /> ส่งออกบัญชี
          </button>
          <button
            onClick={() => setEditing({ mode: 'create' })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 shadow-sm"
          >
            <Plus size={16} /> เพิ่มค่าใช้จ่าย
          </button>
        </div>
      </div>

      {/* Monthly trend line chart */}
      <ExpenseTrendChart data={trend} loading={trendLoading} />

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm mb-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ค้นหา รายละเอียด…"
              className={`${INPUT} w-full pl-8`}
            />
          </div>
          <SellerFilter
            value={sellerId}
            valueName={sellerName}
            onPick={(id, name) => { setSellerId(String(id)); setSellerName(name) }}
            onClear={() => { setSellerId(''); setSellerName('') }}
          />
          <select value={year} onChange={(e) => setYear(e.target.value)} className={INPUT}>
            <option value="">ทุกปี</option>
            {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(e.target.value)} className={INPUT}>
            <option value="">ทั้งปี</option>
            {TH_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className={INPUT}>
            <option value="">ทุกสาขา</option>
            {branches.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <button
            onClick={() => setWht((v) => !v)}
            className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition ${wht ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
          >หัก ณ ที่จ่าย</button>
          <button
            onClick={() => setPetty((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition ${petty ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
          ><Landmark size={13} /> กองกลาง</button>
        </div>
      </div>

      {/* Stat tiles — also the All / จ่ายแล้ว / รอจ่าย selector */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatTile active={paidFilter === 'all'}    onClick={() => setPaidFilter('all')}    label="ทั้งหมด"  count={stats.allCount}    sum={stats.allSum}    tone="brand" />
        <StatTile active={paidFilter === 'paid'}   onClick={() => setPaidFilter('paid')}   label="จ่ายแล้ว" count={stats.paidCount}   sum={stats.paidSum}   tone="emerald" />
        <StatTile active={paidFilter === 'unpaid'} onClick={() => setPaidFilter('unpaid')} label="รอจ่าย"   count={stats.unpaidCount} sum={stats.unpaidSum} tone="slate" />
      </div>
      {truncated && (
        <div className="mb-3 text-[12px] text-amber-600 flex items-center gap-1">
          <AlertCircle size={13} /> แสดงเฉพาะ 5,000 รายการแรก — เลือกเดือน/สาขาเพื่อดูยอดที่ครบถ้วน
        </div>
      )}

      {/* The rows the SCB file will leave out, as a count you can click into.
          Without this the chips below are only findable by paging through the
          whole month. Every flagged row is รอจ่าย by construction, so the tile
          switches with it and the totals above the table stay honest. */}
      {issueCount > 0 && (
        <button
          onClick={() => setOnlyIssues((v) => { if (!v) setPaidFilter('unpaid'); return !v })}
          className={`mb-3 flex w-full items-center gap-2 rounded-xl border px-4 py-2.5 text-left text-[13px] transition ${
            onlyIssues
              ? 'border-amber-300 bg-amber-100/70 text-amber-900'
              : 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100/70'
          }`}
        >
          <AlertTriangle size={15} className="shrink-0" />
          <span className="flex-1">
            <span className="font-semibold tabular-nums">{issueCount.toLocaleString()} รายการ</span>
            {' '}ที่รอจ่ายจะ<span className="font-semibold">ไม่ถูกใส่ในไฟล์โอนเงิน SCB</span> — ข้อมูลบริษัทผู้ขายยังไม่ครบ
          </span>
          <span className="shrink-0 rounded-lg bg-white/70 px-2 py-1 text-[12px] font-medium ring-1 ring-amber-200">
            {onlyIssues ? 'แสดงทั้งหมด' : 'ดูเฉพาะรายการที่มีปัญหา'}
          </span>
        </button>
      )}

      {error && (
        <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-[13px] text-rose-700 flex items-center gap-2">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">วันที่</th>
                <th className="px-5 py-3 font-medium">รายการ / บริษัท</th>
                <th className="px-5 py-3 font-medium">สาขา</th>
                <th className="px-5 py-3 font-medium text-right">ยอดชำระ</th>
                <th className="px-5 py-3 font-medium">สถานะ</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
                </td></tr>
              )}
              {!loading && !items.length && (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-400">ไม่พบรายการค่าใช้จ่าย</td></tr>
              )}
              {!loading && items.map((e) => {
                const amt = e.amount || {}
                const dateStr = e.date_display || e.date_buy
                const hasTax = Number(amt.VAT) > 0 || Number(amt.WHT) > 0
                return (
                  <tr key={e.id} className="group hover:bg-brand-50/40 transition-colors cursor-pointer" onClick={() => setEditing({ mode: 'edit', id: e.id })}>
                    <td className="px-5 py-3 align-top whitespace-nowrap text-slate-500 tabular-nums">{fmtDate(dateStr)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {e.recurring && <Star size={13} className="text-amber-400 fill-amber-400 shrink-0" title="จากรายการโปรด" />}
                        <span className="font-medium text-slate-800 line-clamp-1 max-w-[340px]">{e.detail || <span className="text-slate-400">—</span>}</span>
                        {(e.file_count ?? (Array.isArray(e.file) ? e.file.length : 0)) > 0 && (
                          <span title={`มีไฟล์แนบ ${e.file_count ?? e.file.length} ไฟล์`} className="inline-flex items-center gap-0.5 text-[11px] text-slate-400 shrink-0">
                            <Paperclip size={12} />{e.file_count ?? e.file.length}
                          </span>
                        )}
                        {e.petty_cash && <Tag tone="violet"><Landmark size={10} /> กองกลาง</Tag>}
                        {e.asset && <Tag tone="indigo">สินทรัพย์</Tag>}
                        {!e.tax && <Tag tone="amber">ไม่ลงภาษี</Tag>}
                      </div>
                      {(e.seller?.seller_name || scbIssues[e.id]) && (
                        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {e.seller?.seller_name
                            ? <span className="text-[12px] text-slate-400 line-clamp-1">{e.seller.seller_name}</span>
                            : <span className="text-[12px] text-slate-300">ไม่ได้ระบุบริษัทผู้ขาย</span>}
                          <TransferIssue issue={scbIssues[e.id]} />
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top text-slate-500 whitespace-nowrap">{branchName[String(e.branch_id)] || (e.branch_id ? `#${e.branch_id}` : '—')}</td>
                    <td className="px-5 py-3 align-top text-right whitespace-nowrap">
                      <div className="font-semibold text-slate-800 tabular-nums">{fmtMoney(amt.payment)}</div>
                      {hasTax && (
                        <div className="mt-0.5 text-[10.5px] text-slate-400 tabular-nums space-x-2">
                          {Number(amt.VAT) > 0 && <span>VAT {fmtMoney(amt.VAT)}</span>}
                          {Number(amt.WHT) > 0 && <span>หัก {fmtMoney(amt.WHT)}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top"><StatusBadge paid={!!e.paid} /></td>
                    <td className="px-2 py-3 align-middle text-right">
                      <ChevronRight size={15} className="text-slate-300 group-hover:text-brand-500 transition-colors" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        {pageTotal > 1 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/40">
            <span className="text-[12.5px] text-slate-500 tabular-nums">หน้า {page} / {pageTotal}</span>
            <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
          </div>
        )}
      </div>

      {editing && (() => {
        // Navigate the whole filtered set (across pages), not just the current page.
        const navIndex = editing.mode === 'edit' ? filtered.findIndex((e) => e.id === editing.id) : -1
        const goto = (delta) => {
          const ni = navIndex + delta
          if (ni >= 0 && ni < filtered.length) setEditing({ mode: 'edit', id: filtered[ni].id })
        }
        return (
          <ExpenseEditor
            mode={editing.mode}
            expenseId={editing.id}
            kind={editing.kind || 'expense'}
            branchName={branchName}
            navPos={editing.kind === 'recurring' ? null : (navIndex >= 0 ? { index: navIndex, total: filtered.length } : null)}
            changeSignal={editing.kind === 'recurring' ? 0 : editChangeTick}
            hasPrev={navIndex > 0}
            hasNext={navIndex >= 0 && navIndex < filtered.length - 1}
            onPrev={() => goto(-1)}
            onNext={() => goto(1)}
            onClose={() => { setRecurQueue(null); setEditing(null) }}
            onSaved={() => {
              load(); loadRecurring()
              // "add all" queue → after saving one, roll to the next; else just close.
              if (recurQueue) advanceQueue(recurQueue)
              else setEditing(null)
            }}
            onCopied={(newId, target) => {
              loadRecurring()
              // filter the list to where the copy landed, then open it in the drawer
              setYear(String(target.year))
              setMonth(String(target.month))
              if (target.branch) setBranch(String(target.branch))
              setEditing(newId ? { mode: 'edit', id: newId } : null)
            }}
          />
        )
      })()}

      <RecurringDrawer
        open={recurringOpen}
        onClose={() => setRecurringOpen(false)}
        series={recurringSeries}
        branchName={branchName}
        year={year ? Number(year) : now.getFullYear()}
        month={month ? Number(month) : now.getMonth() + 1}
        mainBranch={branch}
        creatingKey={creatingKey}
        onAddOne={addRecurringOne}
        onAddAll={addRecurringAll}
        onRemove={removeRecurring}
        onOpenEdit={(s) => { setRecurringOpen(false); setEditing({ mode: 'edit', id: s.templateId, kind: 'recurring' }) }}
        onOpenRecord={(id) => { setRecurringOpen(false); setEditing({ mode: 'edit', id }) }}
      />

      {scbOpen && (
        <ExpenseScbModal
          expenseIds={unpaidIds}
          periodLabel={periodLabel}
          scopeLabel={scopeLabel}
          onClose={() => setScbOpen(false)}
        />
      )}
    </div>
  )
}

function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return String(s)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear() + 543}`
}
